import React, { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import TimeSlotsPicker, { TimeSlotInput } from '@/components/meetings/TimeSlotsPicker'
import { meetingService } from '@/services/meetingService'
import { useToast } from '@/contexts/ToastContext'
import { RescheduleMeetingResponse } from '@/types/meeting'

interface RescheduleMeetingModalProps {
  meetingId: string
  isOpen: boolean
  onClose: () => void
  onRescheduled: (result: RescheduleMeetingResponse) => void
}

const MAX_TIME_SLOTS = 5

const RescheduleMeetingModal: React.FC<RescheduleMeetingModalProps> = ({
  meetingId,
  isOpen,
  onClose,
  onRescheduled
}) => {
  const { showToast } = useToast()
  const [timeSlots, setTimeSlots] = useState<TimeSlotInput[]>([{ start: '', end: '' }])
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) return null

  const handleClose = () => {
    if (isSubmitting) return
    setTimeSlots([{ start: '', end: '' }])
    setError('')
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const validSlots = timeSlots.filter(s => s.start && s.end)
    if (validSlots.length === 0) {
      setError('Propose at least one time slot')
      return
    }
    if (validSlots.some(s => new Date(s.end) <= new Date(s.start))) {
      setError("Each slot's end time must be after its start time")
      return
    }
    setError('')
    setIsSubmitting(true)

    try {
      const result = await meetingService.rescheduleMeeting(
        meetingId,
        validSlots.map(s => ({ start: new Date(s.start).toISOString(), end: new Date(s.end).toISOString() }))
      )
      showToast({ type: 'success', message: 'New times proposed' })
      onRescheduled(result)
      handleClose()
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to reschedule meeting'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>

          <h2 className="text-xl font-semibold text-gray-900 mb-1">Reschedule Meeting</h2>
          <p className="text-sm text-gray-500 mb-4">
            Propose new time slots. The other party will be asked to pick one.
          </p>

          <form onSubmit={handleSubmit}>
            <TimeSlotsPicker
              slots={timeSlots}
              onChange={setTimeSlots}
              error={error}
              maxSlots={MAX_TIME_SLOTS}
            />

            <div className="flex gap-3 justify-end border-t pt-4 mt-4">
              <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting} isLoading={isSubmitting}>
                Propose New Times
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default RescheduleMeetingModal
